import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import { useShareBaseUrl, buildShareUrl } from "@/lib/share";
import { AppFrame, TopBar, WhatsAppFAB, BottomNav } from "@/components/Shell";
import Breadcrumbs from "@/components/Breadcrumbs";
import ShareButtons from "@/components/ShareButtons";
import ProductCard from "@/components/ProductCard";

export default function PublicCategory() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(false);
  const shareBase = useShareBaseUrl();

  useEffect(() => {
    (async () => {
      try {
        const s = await api.get("/settings"); setSettings(s.data);
        const r = await api.get(`/public/category/${slug}`);
        setData(r.data);
      } catch { setError(true); }
    })();
  }, [slug]);

  if (error) return (
    <AppFrame>
      <TopBar title="Not found" onBack={false} subtitle="Category" />
      <div className="px-5 mt-8 text-center">
        <div className="font-display-italic text-2xl">this section isn't here</div>
        <Link to="/" className="inline-block mt-4 text-[11px] tracking-editorial-sm uppercase underline underline-offset-[6px]">Back to store</Link>
      </div>
    </AppFrame>
  );

  if (!data) return (
    <AppFrame>
      <TopBar title="Loading..." onBack={false} subtitle="Category" />
    </AppFrame>
  );

  const { category, children, products, breadcrumbs } = data;
  const crumbs = [
    { name: "Home", to: "/" },
    ...breadcrumbs.slice(0, -1).map((c) => ({ name: c.name, to: `/c/${c.slug}` })),
    { name: category.name },
  ];
  const shareUrl = buildShareUrl(shareBase, `/c/${category.slug || slug}`);

  return (
    <AppFrame>
      <TopBar
        title={category.name}
        subtitle="Collection"
        right={<ShareButtons title={category.name} url={shareUrl} testidPrefix="cat-share" />}
      />
      <div className="px-5 mt-3">
        <Breadcrumbs items={crumbs} />
      </div>

      {children.length > 0 && (
        <div className="px-5 mt-5">
          <div className="text-[10px] tracking-editorial uppercase mb-2" style={{ color: "var(--ink-3)" }}>Explore within</div>
          <div className="grid grid-cols-3 gap-2">
            {children.map((c) => (
              <Link
                key={c.id}
                to={`/c/${c.slug}`}
                data-testid={`sub-tile-${c.id}`}
                className="relative aspect-square rounded-2xl overflow-hidden press"
              >
                {c.image ? (
                  <img src={resolveImage(c.image)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0" style={{ background: "var(--bg-alt)" }} />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
                <div className="absolute bottom-2 left-2 right-2 text-white font-display-italic text-[13px] leading-tight">{c.name}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="px-5 mt-6">
        {products.length === 0 ? (
          <div className="rounded-2xl py-14 text-center" style={{ background: "var(--bg-alt)", border: "1px solid rgba(27,25,23,0.06)" }}>
            <div className="font-display-italic text-xl">nothing here yet</div>
            <p className="text-xs mt-1" style={{ color: "var(--ink-2)" }}>New pieces are being curated.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </div>

      <WhatsAppFAB number={settings?.whatsapp_number} />
      <BottomNav />
    </AppFrame>
  );
}
